import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { FiX, FiCopy, FiExternalLink, FiUpload, FiAlertTriangle, FiHexagon } from 'react-icons/fi';
import styles from '../DepositModal/DepositModal.module.css';
import axiosClient from '../../api/axiosClient';
import { showPaymentError } from '../../utils/kycErrorToast';

const WALLET_ADDRESS = '0xaED1E10C93D586fa2d122F369aAa9D409e80d54a';

interface Props {
  onClose: () => void;
  onSuccess?: () => void;
}

const copy = (text: string) => {
  navigator.clipboard?.writeText(text);
  toast.success('Copied to clipboard');
};

const CryptoModal: React.FC<Props> = ({ onClose, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [proof, setProof] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setProof(f);
  };

  const handleSubmit = async () => {
    const amountNum = Number(amount);
    if (!amountNum || amountNum <= 0) { toast.error('Enter a valid amount.'); return; }
    if (!transactionId.trim()) { toast.error('Enter the Transaction Hash / ID.'); return; }
    if (!paymentDate) { toast.error('Select the payment date & time.'); return; }

    setSaving(true);
    try {
      const form = new FormData();
      form.append('type', 'deposit');
      form.append('amount', String(amountNum.toFixed(2)));
      form.append('transactionRef', transactionId);
      form.append('paymentDate', new Date(paymentDate).toISOString());
      form.append('paymentMethodType', 'Crypto Transfer');
      form.append('currency', 'USDT');
      if (proof) form.append('proof', proof);

      await axiosClient.post('/funds', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Deposit request submitted for review.');
      onSuccess?.();
      onClose();
    } catch (err: any) {
      showPaymentError(err, 'Could not submit payment.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <h2>Crypto Deposit</h2>
            <p>Send USDT / BNB and confirm your payment</p>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close"><FiX /></button>
        </div>

        <div className={styles.body}>
          <div className={styles.col}>
            <label className={styles.label}>Amount (USDT)</label>
            <div className={styles.amountInput}>
              <span>$</span>
              <input type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
            </div>

            <label className={styles.label}>Transaction Hash / ID</label>
            <input className={styles.textInput} value={transactionId} onChange={(e) => setTransactionId(e.target.value)} placeholder="0x..." />

            <label className={styles.label}>Payment Date &amp; Time</label>
            <input className={styles.textInput} type="datetime-local" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />

            <div className={styles.panel} style={{ marginTop: 16 }}>
              <div className={styles.panelTitle}><FiUpload /> Upload Payment Proof</div>
              <label className={styles.dropzone}>
                <FiUpload size={22} />
                <span>{proof ? proof.name : 'Click to upload screenshot'}</span>
                <span className={styles.dropzoneHint}>PNG, JPG up to 5MB</span>
                <input type="file" accept="image/png,image/jpeg" hidden onChange={handleFile} />
              </label>
            </div>

            <div className={styles.warningBox}>
              <FiAlertTriangle />
              <div>
                <b>Important</b>
                <p>Processing: 2–6 hours. Double-check the wallet address and network (BNB Chain) before sending.</p>
              </div>
            </div>

            <button className={styles.confirmBtn} onClick={handleSubmit} disabled={saving}>
              {saving ? 'Submitting…' : 'Confirm Payment →'}
            </button>
          </div>

          <div className={styles.col}>
            <div className={styles.panel}>
              <div className={styles.panelTitle}><FiHexagon /> BlockCost Wallet / BNB Chain</div>
              <div className={styles.qrWrap}>
                <img
                  className={styles.qrImg}
                  alt="Wallet QR code"
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(WALLET_ADDRESS)}`}
                />
              </div>
              <div className={styles.upiIdRow} style={{ borderTop: 'none', flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                <span>Use this address to receive assets on BNB Chain.</span>
                <div className={styles.detailValue} style={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 12 }}>
                  <b>{WALLET_ADDRESS}</b>
                  <FiCopy className={styles.copyIcon} onClick={() => copy(WALLET_ADDRESS)} />
                </div>
              </div>
              <a
                className={styles.confirmBtn}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 14, textDecoration: 'none' }}
                href={`https://bscscan.com/address/${WALLET_ADDRESS}`}
                target="_blank"
                rel="noreferrer"
              >
                View on Bscscan <FiExternalLink />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CryptoModal;
